<template>
  <div class="mt-sm">
    <SloTooltip
      :enabled="disabled"
      :text="'Subscription order is being processed and changes to this order can no longer be made.' | t"
      position="top-right"
    >
      <button
        :disabled="disabled"
        @click="modalOpen = true"
        class="flex-1 p-0 bg-transparent c-blue-400 text-left fw-book md:flex-auto hover:c-blue-700"
        type="button"
      >
        {{ 'Update Quantity or Frequency' | t }}
      </button>
    </SloTooltip>
    <transition name="fade-up">
      <SloModal v-if="modalOpen" :maxWidth="500" @dismiss="handleCloseModal">
        <template v-slot:title>
          {{ 'Update subscription details' | t }}
        </template>
        <div class="flex flex-row gap-md justify-between mt-md">
          <SloNumberInput
            :initialValue="currentQuantity"
            :min="1"
            :min-error-text="'You must add a quantity of at least one for an item.' | t"
            :max="999"
            :max-error-text="'You can only order a maximum of 999 quantity for an item.'| t"
            @inputUpdate="handleQuantityInputUpdate($event)"
            @valueUpdate="handleQuantityValueUpdate($event)"
          />
          <SloField class="w-100">
            <SloSelect
              v-model="selectedSubscriptionFrequency"
              :placeholder="'Select a subscription frequency' | t"
            >
              <option
                v-for="(subscriptionFrequency, index) in subscriptionFrequencies"
                :key="`subscription-frequency-${index}`"
                v-html="subscriptionFrequency.name"
                :value="subscriptionFrequency.code"
              />
            </SloSelect>
          </SloField>
        </div>
        <template v-slot:footer>
          <div class="grid grid-cols-1 gap-md w-100 sm:grid-cols-2">
            <SloButton @click="handleCloseModal" :disabled="loading" fill="outline">
             {{ 'Discard Changes' | t }}
            </SloButton>
            <SloButton @click="handleUpdateSubscription" :disabled="loading">
              <SloLoader :loading="loading" size="md" color="white">
                {{ 'Update' | t }}
              </SloLoader>
            </SloButton>
          </div>
        </template>
      </SloModal>
    </transition>
  </div>
</template>

<script>
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloNumberInput from '../../../lib/components/SloNumberInput.vue';
import SloTooltip from '../../../lib/components/SloTooltip.vue';

export default {
  name: 'SubscriptionUpdateButton',
  components: {
    SloNumberInput,
    SloLoader,
    SloLabel,
    SloField,
    SloSelect,
    SloButton,
    SloModal,
    SloTooltip
  },
  filters: {
    t
  },
  props: {
    disabled: {type: Boolean, default: false},
    subscriptionId: {type: String|Number, required: true},
    subscriptionFrequencies: {type: Array, required: true},
    currentQuantity: {type: String|Number, required: true},
    currentSubscriptionFrequencyName: {type: String, required: true}
  },
  data() {
    return {
      modalOpen: false,
      loading: false,
      selectedQuantity: 1,
      selectedSubscriptionFrequency: null
    };
  },
  watch: {
    modalOpen(isOpen) {
      if (isOpen) {
        this.selectedQuantity = this.currentQuantity;
        this.selectedSubscriptionFrequency = this.subscriptionFrequencies.find(
          frequency => frequency.name === this.currentSubscriptionFrequencyName
        )?.code;

        if (!this.selectedQuantity || !this.selectedSubscriptionFrequency) {
          this.modalOpen = false;
          this.$snotify.error(t('No current quantity or frequencies values provided'));
        }
      }
    }
  },
  methods: {
    async handleUpdateSubscription() {
      this.loading = true;

      await marketplaceApi.updateSubscriptionQuantityFrequency(
        this.subscriptionId,
        this.selectedQuantity,
        this.selectedSubscriptionFrequency
      )
        .then(() => {
          this.handleCloseModal();
          this.$emit('subscriptionUpdated');
          this.$snotify.success(t('Subscription details updated'));
        })
        .catch(() => {
          this.loading = false;
          this.$snotify.error(t('Unable to update subscription details'));
        });
    },
    handleQuantityInputUpdate(quantity) {
      this.selectedQuantity = parseInt(quantity) || '';
    },
    handleQuantityValueUpdate(quantity) {
      this.selectedQuantity = quantity;
    },
    handleCloseModal() {
      this.loading = false;
      this.modalOpen = false;
      this.selectedQuantity = 1;
      this.selectedSubscriptionFrequency = null;
    }
  }
};
</script>
